import React, { useEffect, useState } from 'react';
import { Box, Card, CardActions, CardContent, Button, Typography, Chip, Stack, Grid, Collapse, IconButton, List, ListItem, ListItemText, Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Pagination, Alert, FormControlLabel, Switch } from '@mui/material';
import { ExpandLess, ExpandMore, Store, CheckCircle, Error, Warning, Info } from '@mui/icons-material';
import api from '../services/api';
import ResyncDialog from '../components/channels/ResyncDialog';
import ShopifyConnectDialog from '../components/channels/ShopifyConnectDialog';
import type { Product } from '../types';
import TextField from '../components/common/TextField';

/** Solo los canales con API (Mercado Libre, Tienda Nube) se sincronizan; Venta Directa es carga manual. */
const isOnline = (c: { type?: string }) => c.type === 'MercadoLibre' || c.type === 'TiendaNube' || c.type === 'Shopify';

const SYNC_STATUS: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' | 'info' }> = {
  Success: { label: 'Al día', color: 'success' },
  Partial: { label: 'Con órdenes sin grabar', color: 'warning' },
  Error: { label: 'Con error', color: 'error' },
  InProgress: { label: 'Sincronizando…', color: 'info' },
  Pending: { label: 'Sin sincronizar', color: 'default' },
};

const fmtSyncDate = (s?: string | null) => (s ? new Date(s).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', dateStyle: 'short', timeStyle: 'short' }) : '—');

/** Estado de la sincronización y los interruptores de la automática (trae ventas sola; stock al canal opcional). */
const SyncStatusBlock: React.FC<{ channel: any; onChanged: () => void }> = ({ channel: c, onChanged }) => {
  const [saving, setSaving] = useState(false);
  const [resyncOpen, setResyncOpen] = useState(false);
  const status = SYNC_STATUS[c.syncStatus || 'Pending'] ?? { label: c.syncStatus, color: 'default' as const };
  const online = isOnline(c);
  const save = async (patch: { autoSyncEnabled?: boolean; autoPushStock?: boolean }) => {
    if (patch.autoPushStock && !window.confirm('Con esto el stock de DashFlow reemplaza al de las publicaciones cada 10 minutos. Activalo solo si el stock del sistema está al día. ¿Activar?')) return;
    setSaving(true);
    try {
      await api.updateChannelSyncSettings(c.id, { autoSyncEnabled: c.autoSyncEnabled ?? true, autoPushStock: !!c.autoPushStock, ...patch });
      onChanged();
    } finally {
      setSaving(false);
    }
  };
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <Chip size="small" label={status.label} color={status.color} />
        <Typography variant="body2" color="text.secondary">Última: {fmtSyncDate(c.lastSyncAt)}{c.lastSyncOrders ? ` · ${c.lastSyncOrders} órdenes` : ''}</Typography>
      </Stack>
      {c.lastSyncError && <Typography variant="body2" color="error" sx={{ mb: 0.5, wordBreak: 'break-word' }}>{c.lastSyncError}</Typography>}
      {online && c.isConnected && (
        <>
          <FormControlLabel
            control={<Switch size="small" checked={c.autoSyncEnabled ?? true} disabled={saving} onChange={e => save({ autoSyncEnabled: e.target.checked })} />}
            label={<Typography variant="body2">Traer ventas automáticamente (cada 10 min)</Typography>}
          />
          <FormControlLabel
            control={<Switch size="small" checked={!!c.autoPushStock} disabled={saving || c.autoSyncEnabled === false} onChange={e => save({ autoPushStock: e.target.checked })} />}
            label={<Typography variant="body2">Enviar también el stock al canal</Typography>}
          />
          {c.type === 'MercadoLibre' && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                Histórico: {c.historyBackfillNote || (c.historyBackfilledAt ? 'cargado' : 'se carga en la próxima sincronización (órdenes de más de un año)')}
              </Typography>
              {c.historyBackfilledAt && (
                <Button size="small" disabled={saving} onClick={async () => { await api.requestHistoryBackfill(c.id); onChanged(); }}>Volver a traer</Button>
              )}
            </Stack>
          )}
          <Button size="small" disabled={saving} sx={{ mt: 0.5, px: 0 }} onClick={() => setResyncOpen(true)}>
            Resincronizar todo (con revisión)
          </Button>
          {resyncOpen && <ResyncDialog channel={c} onClose={() => setResyncOpen(false)} onApplied={onChanged} />}
          {c.stockAdjustFrom && (
            <Typography variant="caption" color="text.secondary" display="block">
              Las ventas anteriores al {fmtSyncDate(c.stockAdjustFrom)} se importaron sin descontar stock.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export const ChannelsPage: React.FC = () => {
  const [channels, setChannels] = useState<any[]>([]);
  const [logs, setLogs] = useState<Record<number, { ts: string; message: string }[]>>({});
  const [openLog, setOpenLog] = useState<Record<number, boolean>>({});
  const [imports, setImports] = useState<Record<number, string[]>>({});
  const [products, setProducts] = useState<Product[]>([]);
  const [pubDialog, setPubDialog] = useState<{ open: boolean; channelId?: number; loading: boolean; items: any[] }>({ open: false, loading: false, items: [] });
  const [pubSelections, setPubSelections] = useState<Record<string, { productId: number; externalSku?: string; quantity: number }[]>>({});
  const [pubPage, setPubPage] = useState(1);
  const [pubTotalPages, setPubTotalPages] = useState(1);
  const pubPageSize = 50;

  const [shopifyOpen, setShopifyOpen] = useState(false);
  const load = async () => {
    const res = await api.getChannels();
    setChannels(res.items || []);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    (async () => {
      try {
        const res = await api.getProducts({ page: 1, pageSize: 500 });
        setProducts(res.products || []);
      } catch {
        setProducts([]);
      }
    })();
  }, []);

  const connectMl = async (id: number) => {
    const { url } = await api.getMercadoLibreOAuthUrl(id);
    window.location.href = url;
  };
  const connectTn = async (id: number) => {
    const { url } = await api.getTiendaNubeOAuthUrl(id);
    window.location.href = url;
  };
  const sync = async (id: number) => {
    const started = new Date().toLocaleString();
    setLogs((prev) => ({
      ...prev,
      [id]: [{ ts: started, message: 'Sincronizando canal...' }, ...(prev[id] || [])].slice(0, 20),
    }));
    try {
      const res = await api.syncChannel(id);
      const msg = res.error
        ? `ERROR: ${res.error}`
        : `OK ${new Date(res.lastSyncAt || Date.now()).toLocaleString()} · ${res.processed ?? 0} órdenes${res.failed ? `, ${res.failed} con error` : ''}`;
      setImports((prev) => ({ ...prev, [id]: res.ordersImported || [] }));
      if (res.logs && res.logs.length > 0) {
        setLogs((prev) => ({
          ...prev,
          [id]: (res.logs || []).map((m) => ({ ts: new Date().toLocaleString(), message: m })).concat(prev[id] || []).slice(0, 50),
        }));
      }
      setLogs((prev) => ({
        ...prev,
        [id]: [{ ts: new Date().toLocaleString(), message: msg }, ...(prev[id] || [])].slice(0, 20),
      }));
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Error al sincronizar';
      setLogs((prev) => ({
        ...prev,
        [id]: [{ ts: new Date().toLocaleString(), message: `ERROR: ${msg}` }, ...(prev[id] || [])].slice(0, 20),
      }));
    } finally {
      await load();
    }
  };

  const syncFullStock = async (id: number) => {
    const started = new Date().toLocaleString();
    setLogs((prev) => ({
      ...prev,
      [id]: [{ ts: started, message: 'Leyendo stock Full...' }, ...(prev[id] || [])].slice(0, 20),
    }));
    try {
      const res = await api.syncChannelFullStock(id);
      const msg = `Stock Full actualizado ${new Date(res.lastSyncAt || Date.now()).toLocaleString()}`;
      if (res.logs && res.logs.length > 0) {
        setLogs((prev) => ({
          ...prev,
          [id]: (res.logs || []).map((m) => ({ ts: new Date().toLocaleString(), message: m })).concat(prev[id] || []).slice(0, 50),
        }));
      }
      setLogs((prev) => ({
        ...prev,
        [id]: [{ ts: new Date().toLocaleString(), message: msg }, ...(prev[id] || [])].slice(0, 20),
      }));
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Error al leer stock Full';
      setLogs((prev) => ({
        ...prev,
        [id]: [{ ts: new Date().toLocaleString(), message: `ERROR: ${msg}` }, ...(prev[id] || [])].slice(0, 20),
      }));
    } finally {
      await load();
    }
  };

  const disconnect = async (id: number, channelType: string) => {
    if (!window.confirm('¿Estás seguro de desconectar este canal? Deberás volver a autorizar la conexión.')) return;
    try {
      await api.disconnectChannel(id);
      setLogs((prev) => ({
        ...prev,
        [id]: [{ ts: new Date().toLocaleString(), message: 'Canal desconectado' }, ...(prev[id] || [])].slice(0, 20),
      }));
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Error al desconectar';
      setLogs((prev) => ({
        ...prev,
        [id]: [{ ts: new Date().toLocaleString(), message: `ERROR: ${msg}` }, ...(prev[id] || [])].slice(0, 20),
      }));
    } finally {
      await load();
    }
  };

  const reconnect = async (id: number, channelType: string) => {
    if (channelType === 'MercadoLibre') {
      await connectMl(id);
    } else if (channelType === 'TiendaNube') {
      await connectTn(id);
    }
  };

  const loadPublicationsPage = async (channelId: number, pageNum: number) => {
    setPubDialog((prev) => ({ ...prev, loading: true }));
    try {
      const offset = (pageNum - 1) * pubPageSize;
      const res = await api.getMlPublications(channelId, { limit: pubPageSize, offset });
      const total = res.paging?.total || res.items?.length || 0;
      setPubTotalPages(Math.max(1, Math.ceil(total / pubPageSize)));
      setPubDialog({ open: true, channelId, loading: false, items: res.items || [] });
      const initialSelections: Record<string, { productId: number; externalSku?: string; quantity: number }[]> = {};
      (res.items || []).forEach((p: any) => {
        if (p.mappings && Array.isArray(p.mappings)) {
          initialSelections[p.id] = p.mappings.map((m: any) => ({
            productId: m.productId,
            externalSku: m.externalSku,
            quantity: m.quantity || m.quantityInPublication || 1
          }));
        }
      });
      setPubSelections((prev) => ({ ...prev, ...initialSelections }));
    } catch (e) {
      setPubDialog((prev) => ({ ...prev, loading: false, items: [] }));
    }
  };

  const openPublications = async (channelId: number) => {
    setPubDialog({ open: true, channelId, loading: true, items: [] });
    setPubPage(1);
    setPubTotalPages(1);
    setPubSelections({});
    await loadPublicationsPage(channelId, 1);
  };

  const handlePubPageChange = (_: any, page: number) => {
    if (pubDialog.channelId) {
      setPubPage(page);
      loadPublicationsPage(pubDialog.channelId, page);
    }
  };

  const saveMappings = async () => {
    if (!pubDialog.channelId) return;
    setPubDialog((prev) => ({ ...prev, loading: true }));
    try {
      const entries = Object.entries(pubSelections);
      for (const [externalId, maps] of entries) {
        await api.mapMlPublication({ channelId: pubDialog.channelId, externalId, mappings: maps });
      }
    } finally {
      setPubDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h4" fontWeight={700} sx={{ flex: 1 }}>Canales</Typography>
        <Button variant="outlined" onClick={() => setShopifyOpen(true)}>
          {channels.some(c => c.type === 'Shopify') ? 'Reconectar Shopify' : 'Conectar Shopify'}
        </Button>
      </Stack>
      {shopifyOpen && <ShopifyConnectDialog onClose={() => setShopifyOpen(false)} onConnected={() => { setShopifyOpen(false); load(); }} />}
      <Grid container spacing={3}>
        {channels.map((c) => (
          <Grid key={c.id} item xs={12} md={4}>
            <Card>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6">{c.name}</Typography>
                  {isOnline(c)
                    ? <Chip size="small" label={c.isConnected ? 'Conectado' : 'Desconectado'} color={c.isConnected ? 'success' : 'default'} />
                    : <Chip size="small" label="Carga manual" />}
                </Stack>
                {isOnline(c)
                  ? <Typography variant="body2" color="text.secondary">Tipo: {c.type}</Typography>
                  : <Typography variant="body2" color="text.secondary">Ventas que se cargan a mano (mostrador, B2B). No tiene nada que sincronizar.</Typography>}

                {/* Account Info for connected channels */}
                {c.isConnected && c.accountInfo && c.type === 'MercadoLibre' && (
                  <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>Cuenta conectada</Typography>
                    {c.accountInfo.nickname && (
                      <Typography variant="body2">
                        <strong>{c.accountInfo.nickname}</strong>
                        {c.accountInfo.permalink && (
                          <Typography
                            component="a"
                            href={c.accountInfo.permalink}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="caption"
                            sx={{ ml: 1, color: 'primary.main' }}
                          >
                            Ver perfil
                          </Typography>
                        )}
                      </Typography>
                    )}
                    {(c.accountInfo.firstName || c.accountInfo.lastName) && (
                      <Typography variant="body2" color="text.secondary">
                        {[c.accountInfo.firstName, c.accountInfo.lastName].filter(Boolean).join(' ')}
                      </Typography>
                    )}
                    {c.accountInfo.email && (
                      <Typography variant="body2" color="text.secondary">
                        {c.accountInfo.email}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      ID: {c.accountInfo.userId} &bull; Sitio: <strong>{c.accountInfo.siteId || '-'}</strong>
                    </Typography>
                    {c.accountInfo.connectedAt && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Conectado: {new Date(c.accountInfo.connectedAt).toLocaleString()}
                      </Typography>
                    )}
                    {c.accountInfo.tokenExpiresAt && (
                      <Typography
                        variant="caption"
                        display="block"
                        color={new Date(c.accountInfo.tokenExpiresAt) < new Date() ? 'error.main' : 'text.secondary'}
                      >
                        Token expira: {new Date(c.accountInfo.tokenExpiresAt).toLocaleString()}
                        {new Date(c.accountInfo.tokenExpiresAt) < new Date() && ' (EXPIRADO - reconectar)'}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                      {c.accountInfo.hasWarehouseManagement && (
                        <Chip size="small" label="Warehouse Management" color="info" variant="outlined" />
                      )}
                      {c.accountInfo.hasStockDistribuido && (
                        <Chip size="small" label="Stock Distribuido" color="info" variant="outlined" />
                      )}
                    </Stack>
                    {c.accountInfo.stores && c.accountInfo.stores.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Depósitos ({c.accountInfo.stores.length}):
                        </Typography>
                        {c.accountInfo.stores.map((store: any, idx: number) => (
                          <Typography key={idx} variant="caption" display="block" sx={{ pl: 1 }}>
                            <Store sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                            {store.description || store.id} {store.status && `(${store.status})`}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}

                {c.isConnected && c.accountInfo && c.type === 'TiendaNube' && (
                  <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>Cuenta conectada</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Store ID: <strong>{c.accountInfo.storeId}</strong>
                    </Typography>
                    {c.accountInfo.scope && (
                      <Typography variant="body2" color="text.secondary">
                        Permisos: {c.accountInfo.scope}
                      </Typography>
                    )}
                    {c.accountInfo.connectedAt && (
                      <Typography variant="body2" color="text.secondary">
                        Conectado: {new Date(c.accountInfo.connectedAt).toLocaleString()}
                      </Typography>
                    )}
                  </Box>
                )}

                {isOnline(c) && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <SyncStatusBlock channel={c} onChanged={load} />
                  </>
                )}
                {c.isConnected && c.type === 'MercadoLibre' && (
                  <Alert severity="info" icon={<Info fontSize="small" />} sx={{ mt: 1.5, py: 0.5 }}>
                    <Typography variant="caption">
                      Solo se sincronizan productos mapeados. Ve a <strong>Publicaciones</strong> para vincular tus productos de ML con el catálogo local.
                    </Typography>
                  </Alert>
                )}
              </CardContent>
              <CardActions sx={{ px: 2, pb: 2 }}>
                {!c.isConnected && c.type === 'MercadoLibre' && (
                  <Button variant="contained" color="secondary" fullWidth onClick={() => connectMl(c.id)}>Conectar MercadoLibre</Button>
                )}
                {!c.isConnected && c.type === 'TiendaNube' && (
                  <Button variant="contained" color="secondary" fullWidth onClick={() => connectTn(c.id)}>Conectar TiendaNube</Button>
                )}
                {c.isConnected && isOnline(c) && (
                  <Stack spacing={1.5} width="100%">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Button variant="contained" size="small" onClick={() => sync(c.id)} sx={{ flex: 1 }}>Sincronizar</Button>
                      {c.type === 'MercadoLibre' && (
                        <Button variant="outlined" size="small" onClick={() => syncFullStock(c.id)} sx={{ flex: 1 }}>Leer stock Full</Button>
                      )}
                      <IconButton size="small" onClick={() => setOpenLog((prev) => ({ ...prev, [c.id]: !prev[c.id] }))}>
                        {openLog[c.id] ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      {c.type === 'MercadoLibre' && (
                        <Button size="small" variant="outlined" onClick={() => openPublications(c.id)} sx={{ flex: 1 }}>Publicaciones</Button>
                      )}
                      <Button size="small" variant="outlined" color="primary" onClick={() => reconnect(c.id, c.type)} sx={{ flex: 1 }}>Reconectar</Button>
                      <Button size="small" variant="outlined" color="error" onClick={() => disconnect(c.id, c.type)} sx={{ flex: 1 }}>Desconectar</Button>
                    </Stack>
                  </Stack>
                )}
              </CardActions>
              <Collapse in={!!openLog[c.id]}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>Log de sincronización</Typography>
                  {(logs[c.id] || []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">Sin eventos todavía</Typography>
                  ) : (
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell width="5%"></TableCell>
                            <TableCell width="20%">Hora</TableCell>
                            <TableCell>Detalle</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(logs[c.id] || []).map((l, idx) => {
                            const isOk = l.message.startsWith('OK') || l.message.includes('Importada') || l.message.includes('actualizado') || l.message.includes('ajustado');
                            const isError = l.message.startsWith('ERROR') || l.message.includes('Error');
                            const isOmitida = l.message.startsWith('Omitida');
                            const isProgress = l.message.includes('Sincronizando') || l.message.includes('Leyendo');

                            let bgColor = 'inherit';
                            let icon = <Info sx={{ fontSize: 16, color: 'text.secondary' }} />;

                            if (isOk) {
                              bgColor = 'rgba(46, 125, 50, 0.08)';
                              icon = <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />;
                            } else if (isError) {
                              bgColor = 'rgba(211, 47, 47, 0.08)';
                              icon = <Error sx={{ fontSize: 16, color: 'error.main' }} />;
                            } else if (isOmitida) {
                              bgColor = 'rgba(237, 108, 2, 0.08)';
                              icon = <Warning sx={{ fontSize: 16, color: 'warning.main' }} />;
                            } else if (isProgress) {
                              bgColor = 'rgba(2, 136, 209, 0.08)';
                              icon = <Info sx={{ fontSize: 16, color: 'info.main' }} />;
                            }

                            // Parsear mensaje de omitida para mostrar mejor
                            let displayMessage = l.message;
                            let productInfo = null;
                            if (isOmitida && l.message.includes(':')) {
                              const parts = l.message.split(': ');
                              const orderPart = parts[0]; // "Omitida ML 2000014518337134"
                              const detailPart = parts.slice(1).join(': '); // resto del mensaje

                              // Extraer info del producto si tiene el formato esperado
                              const match = detailPart.match(/^(.+?) \(SKU: (.+?), ID: (.+?), x(\d+)\)$/);
                              if (match) {
                                productInfo = {
                                  title: match[1],
                                  sku: match[2],
                                  mlId: match[3],
                                  qty: match[4]
                                };
                                displayMessage = orderPart;
                              }
                            }

                            return (
                              <TableRow key={`${c.id}-${idx}`} sx={{ bgcolor: bgColor }}>
                                <TableCell sx={{ py: 0.5 }}>{icon}</TableCell>
                                <TableCell sx={{ py: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary">{l.ts}</Typography>
                                </TableCell>
                                <TableCell sx={{ py: 0.5 }}>
                                  {productInfo ? (
                                    <Box>
                                      <Typography variant="body2" component="span" fontWeight={500}>
                                        {displayMessage}
                                      </Typography>
                                      <Box sx={{ mt: 0.5, pl: 1, borderLeft: '2px solid', borderColor: 'warning.main' }}>
                                        <Typography variant="body2">{productInfo.title}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          SKU: {productInfo.sku} • ML ID: {productInfo.mlId} • Cant: {productInfo.qty}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  ) : (
                                    <Typography variant="body2">{displayMessage}</Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  {(imports[c.id] || []).length > 0 && (
                    <>
                      <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Órdenes importadas ({imports[c.id].length})</Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {(imports[c.id] || []).map((ref, idx) => (
                          <Chip key={`${c.id}-imp-${idx}`} label={ref} size="small" color="success" variant="outlined" />
                        ))}
                      </Stack>
                    </>
                  )}
                </CardContent>
              </Collapse>
            </Card>
          </Grid>
        ))}
        {channels.length === 0 && (
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">No hay canales configurados en este tenant.</Typography>
          </Grid>
        )}
      </Grid>

      <Dialog open={pubDialog.open} onClose={() => setPubDialog({ open: false, loading: false, items: [] })} maxWidth="lg" fullWidth>
        <DialogTitle>Publicaciones MercadoLibre</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Importante:</strong> Para sincronizar stock y órdenes, debes vincular cada publicación de ML con un producto de tu catálogo local.
              Las publicaciones sin mapeo no sincronizarán stock ni importarán órdenes correctamente.
            </Typography>
          </Alert>
          {pubDialog.loading && <Typography variant="body2">Cargando publicaciones...</Typography>}
          {!pubDialog.loading && pubDialog.items.length === 0 && (
            <Typography variant="body2" color="text.secondary">No se encontraron publicaciones.</Typography>
          )}
          <Stack spacing={2}>
            {pubDialog.items.map((p) => {
              const selections = pubSelections[p.id] || [];
              return (
                <Card key={p.id} variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600}>{p.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{p.id}</Typography>
                    {p.permalink && <Typography variant="body2" color="primary">{p.permalink}</Typography>}
                    <Typography variant="body2" color="text.secondary">SKU publicación: {p.sku || '—'}</Typography>
                    {p.variations?.length > 0 && (
                      <Typography variant="body2" color="text.secondary">Variantes: {p.variations.map((v: any) => v.sku || v.id).filter(Boolean).join(', ') || '—'}</Typography>
                    )}
                    <Typography variant="subtitle2" sx={{ mt: 1 }}>Mapear productos locales</Typography>
                    <Autocomplete
                      multiple
                      options={products}
                      getOptionLabel={(op) => `${op.sku} — ${op.name}`}
                      value={products.filter((prod) => selections.some((s) => s.productId === prod.id))}
                      onChange={(_, vals) => {
                        setPubSelections((prev) => {
                          const existing = prev[p.id] || [];
                          // Mantener cantidades existentes, agregar nuevos con cantidad 1
                          const newSelections = vals.map((v) => {
                            const existingSel = existing.find((e) => e.productId === v.id);
                            return {
                              productId: v.id,
                              externalSku: v.sku,
                              quantity: existingSel?.quantity || 1
                            };
                          });
                          return { ...prev, [p.id]: newSelections };
                        });
                      }}
                      renderInput={(params) => <TextField {...params} label="Productos" placeholder="Buscar..." />}
                    />
                    {selections.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                          Cantidad de cada producto en esta publicación (para kits/combos):
                        </Typography>
                        <Stack spacing={1}>
                          {selections.map((s) => {
                            const prod = products.find((pr) => pr.id === s.productId);
                            return (
                              <Stack key={s.productId} direction="row" spacing={2} alignItems="center">
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  {prod ? `${prod.sku} — ${prod.name}` : `Producto #${s.productId}`}
                                </Typography>
                                <TextField
                                  type="number"
                                  size="small"
                                  label="Cant."
                                  value={s.quantity}
                                  onChange={(e) => {
                                    const qty = Math.max(1, parseInt(e.target.value) || 1);
                                    setPubSelections((prev) => ({
                                      ...prev,
                                      [p.id]: prev[p.id].map((sel) =>
                                        sel.productId === s.productId ? { ...sel, quantity: qty } : sel
                                      )
                                    }));
                                  }}
                                  inputProps={{ min: 1, style: { width: 60 } }}
                                  sx={{ width: 100 }}
                                />
                              </Stack>
                            );
                          })}
                        </Stack>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
          {pubTotalPages > 1 && (
            <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
              <Pagination
                count={pubTotalPages}
                page={pubPage}
                onChange={handlePubPageChange}
                color="primary"
                disabled={pubDialog.loading}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPubDialog({ open: false, loading: false, items: [] })}>Cerrar</Button>
          <Button variant="contained" disabled={pubDialog.loading || !pubDialog.channelId} onClick={saveMappings}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
